import { AccountMetricsChart } from '@/components/Charts/AccountMetricsChart'
import { AdMetricsChart } from '@/components/Charts/AdMetricsChart'
import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import {
  AlertCircle,
  BarChart3,
  CheckCircle,
  ChevronDown,
  DollarSign,
  Download,
  Eye,
  FileDown,
  Lightbulb,
  MousePointer,
  Percent,
  Presentation,
  Shield,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import { SiMeta } from 'react-icons/si'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsInsights,
  type FacebookAdsInsightsResult,
} from 'sprites-types/workflow/tools/facebookAdsInsights'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

// Priority badge component
function PriorityBadge({ priority }: { priority: 'high' | 'medium' | 'low' }) {
  const styles = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200',
  }

  const icons = {
    high: '🔴',
    medium: '🟡',
    low: '🟢',
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${styles[priority]}`}
    >
      <span>{icons[priority]}</span>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  )
}

// Category badge component
function CategoryBadge({ category }: { category: string }) {
  const styles: Record<string, string> = {
    Performance: 'bg-blue-100 text-blue-700 border-blue-200',
    Budget: 'bg-purple-100 text-purple-700 border-purple-200',
    Targeting: 'bg-orange-100 text-orange-700 border-orange-200',
    Creative: 'bg-pink-100 text-pink-700 border-pink-200',
  }

  const style = styles[category] || 'bg-gray-100 text-gray-700 border-gray-200'

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {category}
    </span>
  )
}

// Utility functions
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const formatPercent = (value: number) => {
  return `${value.toFixed(2)}%`
}

function FacebookAdsInsightsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({})
  const [expandedChart, setExpandedChart] = useState<string | null>(null)
  const [isExportingPdf, setIsExportingPdf] = useState(false)

  // Parse the result using shared schema
  const schema = getToolResultSchema(facebookAdsInsights, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  // Handle both emitMode: 'single' and 'multi' (array of results)
  const data = parseResult.data
  const result: FacebookAdsInsightsResult = Array.isArray(data) ? data[0] : data

  const toggleInsight = (index: string) => {
    setExpandedInsights((prev) => ({ ...prev, [index]: !prev[index] }))
  }

  const handleExportToPdf = async () => {
    setIsExportingPdf(true)

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/insights-pdf-export`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            insightsData: result,
            filename: `Meta_Ads_Report_${new Date().toISOString().split('T')[0]}`,
          }),
        }
      )

      if (!response.ok) {
        throw new Error('Failed to export PDF')
      }

      const { url } = await response.json()

      if (url) {
        // Open PDF in new tab instead of downloading
        window.open(url, '_blank')

        toast.success('🎉 PDF report opened in new tab!')
      } else {
        throw new Error('No download URL received')
      }
    } catch (error) {
      console.error('PDF export failed:', error)
      toast.error('Failed to export PDF. Please try again.')
    } finally {
      setIsExportingPdf(false)
    }
  }

  // Create context menu actions
  const contextActions: ContextAction[] = [
    {
      id: 'export-pdf',
      label: isExportingPdf ? 'Exporting...' : 'Export to Slide Show',
      icon: isExportingPdf ? (
        <Download className="h-4 w-4 animate-pulse" />
      ) : (
        <Presentation className="h-4 w-4" />
      ),
      onClick: handleExportToPdf,
      disabled: isExportingPdf,
    },
  ]

  // Group insights by priority
  const highPriorityInsights = result.insights.filter((i) => i.priority === 'high')
  const mediumPriorityInsights = result.insights.filter((i) => i.priority === 'medium')
  const lowPriorityInsights = result.insights.filter((i) => i.priority === 'low')

  // Determine entity context for chart title
  const getEntityContext = ():
    | { type: 'account' | 'campaign' | 'adset' | 'ad'; name?: string }
    | undefined => {
    const performanceData = result.performanceData
    if (!performanceData) return undefined

    // Check if focusing on a single entity
    if (performanceData.campaigns?.length === 1) {
      return { type: 'campaign', name: performanceData.campaigns[0].campaign_name }
    }
    if (performanceData.adsets?.length === 1 && !performanceData.campaigns?.length) {
      return { type: 'adset', name: performanceData.adsets[0].adset_name }
    }
    if (performanceData.ads?.length === 1 && !performanceData.adsets?.length) {
      return { type: 'ad', name: performanceData.ads[0].ad_name }
    }

    return { type: 'account' }
  }

  // Calculate aggregated metrics from performance data
  // Only aggregate from the lowest/most granular level to avoid double-counting
  const calculateMetrics = () => {
    const performanceData = result.performanceData
    if (!performanceData) return null

    // Determine which entity level to aggregate from
    // Use the most granular level available to avoid counting the same metrics multiple times
    // Hierarchy: Ads (most granular) > Ad Sets > Campaigns
    let entitiesToAggregate: any[] = []

    if (performanceData.ads && performanceData.ads.length > 0) {
      // Use ads if available (most granular)
      entitiesToAggregate = performanceData.ads
    } else if (performanceData.adsets && performanceData.adsets.length > 0) {
      // Use ad sets if no ads available
      entitiesToAggregate = performanceData.adsets
    } else if (performanceData.campaigns && performanceData.campaigns.length > 0) {
      // Use campaigns if no ad sets or ads
      entitiesToAggregate = performanceData.campaigns
    }

    if (entitiesToAggregate.length === 0) return null

    const totals = entitiesToAggregate.reduce(
      (acc, entity) => {
        acc.impressions += entity.impressions || 0
        acc.clicks += entity.clicks || 0
        acc.spend += entity.spend || 0
        acc.revenue += entity.revenue || 0
        acc.conversions += entity.conversions || 0

        // Accumulate Meta ROAS with weighted average by spend
        if (entity.meta_roas && entity.spend > 0) {
          acc.metaRoasWeightedSum += entity.meta_roas * entity.spend
          acc.metaRoasWeight += entity.spend
        }

        return acc
      },
      {
        impressions: 0,
        clicks: 0,
        spend: 0,
        revenue: 0,
        conversions: 0,
        metaRoasWeightedSum: 0,
        metaRoasWeight: 0,
      }
    )

    const avgCTR = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0
    const avgCPC = totals.clicks > 0 ? totals.spend / totals.clicks : 0
    const avgCPM = totals.impressions > 0 ? (totals.spend / totals.impressions) * 1000 : 0
    const roas = totals.spend > 0 ? totals.revenue / totals.spend : null
    const metaRoas =
      totals.metaRoasWeight > 0 ? totals.metaRoasWeightedSum / totals.metaRoasWeight : null

    return {
      impressions: totals.impressions,
      clicks: totals.clicks,
      spend: totals.spend,
      revenue: totals.revenue,
      conversions: totals.conversions,
      avgCTR,
      avgCPC,
      avgCPM,
      roas,
      metaRoas,
      campaigns: performanceData.campaigns?.length || 0,
      adsets: performanceData.adsets?.length || 0,
      ads: performanceData.ads?.length || 0,
    }
  }

  const metrics = calculateMetrics()

  return (
    <div className="space-y-3">
      {/* Header */}
      <ContextMenuWrapper actions={contextActions}>
        <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <SiMeta className="h-5 w-5 text-[#0064e0]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900">
                Meta Ads Insights & Recommendations
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {result.insights.length} insights • {highPriorityInsights.length} high priority
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleExportToPdf()
                }}
                disabled={isExportingPdf}
                className="group/export flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#fd9543] to-[#f996cd] px-3 py-1.5 text-xs font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg hover:shadow-orange-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isExportingPdf ? (
                  <>
                    <Download className="h-3.5 w-3.5 animate-pulse" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <FileDown className="h-3.5 w-3.5 group-hover/export:animate-bounce" />
                    <span>Export PDF Report</span>
                  </>
                )}
              </button>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>

          {isExpanded && (
            <div className="border-t border-gray-200/50 px-4 py-4">
              {/* Performance Statistics */}
              {metrics && (
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-900">Performance Statistics</div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  {/* Entity Counts */}
                  <div className="mb-4 flex flex-wrap gap-2">
                    {metrics.campaigns > 0 && (
                      <div className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                        {metrics.campaigns} Campaign{metrics.campaigns !== 1 ? 's' : ''}
                      </div>
                    )}
                    {metrics.adsets > 0 && (
                      <div className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
                        {metrics.adsets} Ad Set{metrics.adsets !== 1 ? 's' : ''}
                      </div>
                    )}
                    {metrics.ads > 0 && (
                      <div className="rounded-full bg-pink-100 px-3 py-1 text-xs font-medium text-pink-700">
                        {metrics.ads} Ad{metrics.ads !== 1 ? 's' : ''}
                      </div>
                    )}
                    {result.performanceData?.dateRange && (
                      <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                        {result.performanceData.dateRange.start} to{' '}
                        {result.performanceData.dateRange.end}
                      </div>
                    )}
                  </div>

                  {/* Metrics Grid - Ordered by priority: REVENUE, AD SPEND, ROAS, # OF AD CONVERSIONS, META ROAS, then rest */}
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {/* PRIORITY 1: REVENUE */}
                    {metrics.revenue > 0 && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-medium text-gray-700">Total Revenue</span>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          {formatCurrency(metrics.revenue)}
                        </div>
                      </div>
                    )}

                    {/* PRIORITY 2: AD SPEND */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Total Spend</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(metrics.spend)}
                      </div>
                    </div>

                    {/* PRIORITY 3: ROAS */}
                    {metrics.roas !== null && metrics.roas !== undefined && metrics.roas > 0 && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-medium text-gray-700">ROAS</span>
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                          {metrics.roas.toFixed(2)}x
                        </div>
                      </div>
                    )}

                    {/* PRIORITY 4: # OF AD CONVERSIONS */}
                    {metrics.conversions > 0 && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-gray-600" />
                          <span className="text-xs font-medium text-gray-700">Conversions</span>
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          {formatNumber(metrics.conversions)}
                        </div>
                      </div>
                    )}

                    {/* PRIORITY 5: META ROAS */}
                    {metrics.metaRoas !== null &&
                      metrics.metaRoas !== undefined &&
                      metrics.metaRoas > 0 && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="mb-1 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-purple-600" />
                            <span className="text-xs font-medium text-gray-700">Meta ROAS</span>
                          </div>
                          <div className="text-lg font-bold text-purple-600">
                            {metrics.metaRoas.toFixed(2)}x
                          </div>
                        </div>
                      )}

                    {/* REST: Other metrics in order of importance */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <Eye className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Total Impressions</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatNumber(metrics.impressions)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <MousePointer className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Total Clicks</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatNumber(metrics.clicks)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <Percent className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Avg CTR</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatPercent(metrics.avgCTR)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Avg CPC</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(metrics.avgCPC)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Avg CPM</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(metrics.avgCPM)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Executive Summary */}
              <div className="mb-6 overflow-hidden rounded-xl border border-blue-200/60 bg-white shadow-sm">
                <div className="border-b border-blue-100 bg-gradient-to-r from-blue-50 to-blue-50/50 px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0064e0]">
                      <Lightbulb className="h-4.5 w-4.5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-gray-900">Executive Summary</div>
                      <div className="text-xs text-gray-600">
                        Overview of your Facebook Ads performance
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-5 py-5">
                  <div className="text-sm leading-relaxed text-gray-800">
                    <EnhancedMarkdown className="prose prose-sm prose-headings:mt-5 prose-headings:mb-3 prose-headings:font-bold prose-headings:text-gray-900 prose-h3:text-base prose-h3:border-b prose-h3:border-gray-200 prose-h3:pb-2 prose-p:my-3 prose-p:leading-relaxed prose-p:text-gray-700 prose-strong:font-semibold prose-strong:text-gray-900 prose-ul:my-3 prose-ul:ml-1 prose-ul:space-y-1.5 prose-li:text-gray-700 prose-li:pl-1 prose-li:leading-relaxed max-w-none [&>h3:first-child]:mt-0">
                      {result.summary}
                    </EnhancedMarkdown>
                  </div>
                  <AccountMetricsChart platform="facebook" entityContext={getEntityContext()} />
                </div>

                {/* Quick Stats */}
                <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3">
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-gray-200/50">
                      <CheckCircle className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-xs font-medium text-gray-700">
                        {result.insights.length} Insights
                      </span>
                    </div>
                    {highPriorityInsights.length > 0 && (
                      <div className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-red-200/50">
                        <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                        <span className="text-xs font-medium text-gray-700">
                          {highPriorityInsights.length} High Priority
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Performance Highlights */}
              {((result.topPerformers && result.topPerformers.length > 0) ||
                (result.underperformers && result.underperformers.length > 0)) && (
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-900">Performance Highlights</div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  <div className="space-y-4">
                    {/* Top Performers */}
                    {result.topPerformers && result.topPerformers.length > 0 && (
                      <div className="overflow-hidden rounded-xl border-2 border-green-300 bg-gradient-to-br from-green-50 to-green-100/50 shadow-md">
                        <div className="border-b border-green-200 bg-green-100/80 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="rounded-lg bg-green-600 p-1.5">
                              <TrendingUp className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-green-900">Top Performers</div>
                              <div className="text-xs text-green-700">
                                Strong performance driving results
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3 p-4">
                          {result.topPerformers.map((performer, index) => (
                            <div
                              key={index}
                              className="rounded-lg border border-green-300 bg-white p-3 shadow-sm"
                            >
                              <div className="mb-1.5 flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-xs font-semibold text-gray-900">
                                    {performer.name}
                                  </div>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                  <div className="text-xs font-bold text-green-700">
                                    {performer.metric}
                                  </div>
                                  <div className="text-xs font-semibold text-green-600">
                                    {typeof performer.value === 'number'
                                      ? performer.value.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })
                                      : performer.value}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs leading-relaxed text-gray-600 [&_p]:text-xs [&_p]:leading-relaxed [&_strong]:font-semibold">
                                {performer.description ? (
                                  <EnhancedMarkdown>{performer.description}</EnhancedMarkdown>
                                ) : (
                                  `Excelling with strong ${performer.metric.toLowerCase()} performance, driving efficient results for the account`
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Underperformers */}
                    {result.underperformers && result.underperformers.length > 0 && (
                      <div className="overflow-hidden rounded-xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100/50 shadow-md">
                        <div className="border-b border-orange-200 bg-orange-100/80 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="rounded-lg bg-orange-600 p-1.5">
                              <TrendingDown className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-orange-900">
                                Needs Attention
                              </div>
                              <div className="text-xs text-orange-700">
                                Opportunities for optimization
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3 p-4">
                          {result.underperformers.map((performer, index) => (
                            <div
                              key={index}
                              className="rounded-lg border border-orange-300 bg-white p-3 shadow-sm"
                            >
                              <div className="mb-1.5 flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-xs font-semibold text-gray-900">
                                    {performer.name}
                                  </div>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                  <div className="text-xs font-bold text-orange-700">
                                    {performer.metric}
                                  </div>
                                  <div className="text-xs font-semibold text-orange-600">
                                    {typeof performer.value === 'number'
                                      ? performer.value.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })
                                      : performer.value}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs leading-relaxed text-gray-600 [&_p]:text-xs [&_p]:leading-relaxed [&_strong]:font-semibold">
                                {performer.description ? (
                                  <EnhancedMarkdown>{performer.description}</EnhancedMarkdown>
                                ) : (
                                  `Underperforming with ${performer.metric.toLowerCase()} below benchmarks, requires attention and optimization`
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Suggested Changes */}
              {result.insights.length > 0 && (
                <div>
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-900">Suggested Changes</div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  {/* High Priority Insights */}
                  {highPriorityInsights.length > 0 && (
                    <div className="mb-5">
                      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        🔴 High Priority
                      </div>
                      <div className="space-y-3">
                        {highPriorityInsights.map((insight, index) => (
                          <InsightCard
                            key={`high-${index}`}
                            insight={insight}
                            isExpanded={expandedInsights[`high-${index}`] || false}
                            onToggle={() => toggleInsight(`high-${index}` as any)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Medium Priority Insights */}
                  {mediumPriorityInsights.length > 0 && (
                    <div className="mb-5">
                      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        🟡 Medium Priority
                      </div>
                      <div className="space-y-3">
                        {mediumPriorityInsights.map((insight, index) => (
                          <InsightCard
                            key={`medium-${index}`}
                            insight={insight}
                            isExpanded={expandedInsights[`medium-${index}`] || false}
                            onToggle={() => toggleInsight(`medium-${index}` as any)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Low Priority Insights */}
                  {lowPriorityInsights.length > 0 && (
                    <div className="mb-3">
                      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        🟢 Low Priority
                      </div>
                      <div className="space-y-3">
                        {lowPriorityInsights.map((insight, index) => (
                          <InsightCard
                            key={`low-${index}`}
                            insight={insight}
                            isExpanded={expandedInsights[`low-${index}`] || false}
                            onToggle={() => toggleInsight(`low-${index}` as any)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Entity Performance Breakdown */}
              {result.performanceData &&
                (result.performanceData.campaigns?.length ||
                  result.performanceData.adsets?.length ||
                  result.performanceData.ads?.length) && (
                  <div className="mb-6">
                    <div className="mb-4 flex items-center gap-2">
                      <div className="text-sm font-bold text-gray-900">
                        Entity Performance Breakdown
                      </div>
                      <div className="h-px flex-1 bg-gray-200"></div>
                    </div>

                    {/* Campaigns */}
                    {result.performanceData.campaigns &&
                      result.performanceData.campaigns.length > 0 && (
                        <EntitySection
                          title="Campaigns"
                          entities={result.performanceData.campaigns}
                          type="campaign"
                          expandedChart={expandedChart}
                          onChartToggle={setExpandedChart}
                        />
                      )}

                    {/* Ad Sets */}
                    {result.performanceData.adsets && result.performanceData.adsets.length > 0 && (
                      <EntitySection
                        title="Ad Sets"
                        entities={result.performanceData.adsets}
                        type="adset"
                        expandedChart={expandedChart}
                        onChartToggle={setExpandedChart}
                      />
                    )}

                    {/* Ads */}
                    {result.performanceData.ads && result.performanceData.ads.length > 0 && (
                      <EntitySection
                        title="Ads"
                        entities={result.performanceData.ads}
                        type="ad"
                        expandedChart={expandedChart}
                        onChartToggle={setExpandedChart}
                      />
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      </ContextMenuWrapper>
    </div>
  )
}

// Individual insight card component
function InsightCard({
  insight,
  isExpanded,
  onToggle,
}: {
  insight: FacebookAdsInsightsResult['insights'][0]
  isExpanded: boolean
  onToggle: () => void
}) {
  // Priority-based styling
  const priorityStyles = {
    high: {
      border: 'border-red-300',
      shadow: 'shadow-md shadow-red-100/50',
      accent: 'border-l-4 border-l-red-500',
      bg: 'bg-red-50/30',
      hoverBg: 'hover:bg-red-50/50',
      icon: 'text-red-600',
      iconBg: 'bg-red-100',
      expandedBg: 'bg-red-50/40',
      expandedBorder: 'border-red-200',
    },
    medium: {
      border: 'border-yellow-300',
      shadow: 'shadow-md shadow-yellow-100/50',
      accent: 'border-l-4 border-l-yellow-500',
      bg: 'bg-yellow-50/30',
      hoverBg: 'hover:bg-yellow-50/50',
      icon: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      expandedBg: 'bg-yellow-50/40',
      expandedBorder: 'border-yellow-200',
    },
    low: {
      border: 'border-green-300',
      shadow: 'shadow shadow-green-100/50',
      accent: 'border-l-4 border-l-green-500',
      bg: 'bg-green-50/20',
      hoverBg: 'hover:bg-green-50/40',
      icon: 'text-green-600',
      iconBg: 'bg-green-100',
      expandedBg: 'bg-green-50/30',
      expandedBorder: 'border-green-200',
    },
  }

  const styles = priorityStyles[insight.priority]

  return (
    <div
      className={`overflow-hidden rounded-2xl ${styles.border} ${styles.shadow} ${styles.accent} ${styles.bg} transition-all duration-200`}
    >
      <button
        onClick={onToggle}
        className={`flex w-full items-start gap-3 p-4 text-left transition-colors ${styles.hoverBg}`}
      >
        <div className="flex-shrink-0 pt-0.5">
          <div className={`rounded-lg p-1.5 ${styles.iconBg}`}>
            {insight.priority === 'high' ? (
              <AlertCircle className={`h-4.5 w-4.5 ${styles.icon}`} />
            ) : insight.priority === 'medium' ? (
              <Lightbulb className={`h-4.5 w-4.5 ${styles.icon}`} />
            ) : (
              <CheckCircle className={`h-4.5 w-4.5 ${styles.icon}`} />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <CategoryBadge category={insight.category} />
            <PriorityBadge priority={insight.priority} />
          </div>
          <div className="text-sm font-semibold leading-relaxed text-gray-900">
            <EnhancedMarkdown className="prose prose-sm prose-p:font-semibold prose-p:leading-relaxed max-w-none">
              {insight.finding}
            </EnhancedMarkdown>
          </div>
        </div>
        <div className="flex-shrink-0">
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isExpanded && (
        <div className={`border-t ${styles.expandedBorder} ${styles.expandedBg} px-4 py-4`}>
          <div className="mb-3 rounded-lg border border-blue-200 bg-white/80 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-1.5">
              <div className="rounded bg-blue-100 px-2 py-0.5">
                <span className="text-xs font-bold text-blue-700">💡 ACTION</span>
              </div>
            </div>
            <div className="text-sm font-medium leading-relaxed text-gray-800">
              <EnhancedMarkdown className="prose prose-sm prose-p:font-medium prose-p:leading-relaxed prose-strong:font-bold prose-strong:text-gray-900 max-w-none">
                {insight.recommendation}
              </EnhancedMarkdown>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white/60 p-3">
            <div className="mb-1.5 text-xs font-semibold text-gray-600">📈 Expected Impact</div>
            <div className="text-sm leading-relaxed text-gray-700">
              <EnhancedMarkdown className="prose prose-sm prose-p:leading-relaxed max-w-none">
                {insight.impact}
              </EnhancedMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Entity Section Component - Shows a collapsible section of entities
function EntitySection({
  title,
  entities,
  type,
  expandedChart,
  onChartToggle,
}: {
  title: string
  entities: any[]
  type: 'campaign' | 'adset' | 'ad'
  expandedChart?: string | null
  onChartToggle?: (entityId: string | null) => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showNoStats, setShowNoStats] = useState(false)

  const typeColors = {
    campaign: 'bg-blue-100 text-blue-800 border-blue-200',
    adset: 'bg-purple-100 text-purple-800 border-purple-200',
    ad: 'bg-pink-100 text-pink-800 border-pink-200',
  }

  // Sort entities: those with stats first (sorted by performance), then those without
  const sortedEntities = [...entities].sort((a, b) => {
    const aHasStats = (a.impressions || 0) > 0 || (a.clicks || 0) > 0 || (a.spend || 0) > 0
    const bHasStats = (b.impressions || 0) > 0 || (b.clicks || 0) > 0 || (b.spend || 0) > 0

    // Entities without stats go to the end
    if (aHasStats && !bHasStats) return -1
    if (!aHasStats && bHasStats) return 1
    if (!aHasStats && !bHasStats) return 0

    // For entities with stats, sort by spend (descending) - most important/active first
    // Spend is a reliable, consistent metric that indicates priority
    return (b.spend || 0) - (a.spend || 0)
  })

  // Separate entities with and without stats
  const entitiesWithStats = sortedEntities.filter(
    (e) => (e.impressions || 0) > 0 || (e.clicks || 0) > 0 || (e.spend || 0) > 0
  )
  const entitiesWithoutStats = sortedEntities.filter(
    (e) => (e.impressions || 0) === 0 && (e.clicks || 0) === 0 && (e.spend || 0) === 0
  )

  const visibleEntities = showNoStats ? sortedEntities : entitiesWithStats

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
      >
        <div className="flex items-center gap-3">
          <span
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${typeColors[type]}`}
          >
            {title}
          </span>
          <span className="text-sm font-medium text-gray-700">
            {entitiesWithStats.length} active
            {entitiesWithoutStats.length > 0 && (
              <span className="text-gray-500"> • {entitiesWithoutStats.length} inactive</span>
            )}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {isExpanded && (
        <div className="mt-3">
          <div className="space-y-2">
            {visibleEntities.map((entity) => {
              const hasStats =
                (entity.impressions || 0) > 0 || (entity.clicks || 0) > 0 || (entity.spend || 0) > 0
              // Use the appropriate ID field based on entity type
              const entityId =
                type === 'campaign'
                  ? entity.campaign_id
                  : type === 'adset'
                    ? entity.adset_id
                    : entity.ad_id
              return (
                <EntityCard
                  key={entityId || `${type}-${entity.name || Math.random()}`}
                  entity={entity}
                  type={type}
                  hasStats={hasStats}
                  expandedChart={expandedChart}
                  onChartToggle={onChartToggle}
                />
              )
            })}
          </div>

          {entitiesWithoutStats.length > 0 && !showNoStats && (
            <button
              onClick={() => setShowNoStats(true)}
              className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              Show {entitiesWithoutStats.length} inactive {title.toLowerCase()} with no stats
            </button>
          )}

          {entitiesWithoutStats.length > 0 && showNoStats && (
            <button
              onClick={() => setShowNoStats(false)}
              className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              Hide {entitiesWithoutStats.length} inactive {title.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Entity Card Component - Shows individual entity performance
function EntityCard({
  entity,
  type,
  hasStats,
  expandedChart,
  onChartToggle,
}: {
  entity: any
  type: 'campaign' | 'adset' | 'ad'
  hasStats: boolean
  expandedChart?: string | null
  onChartToggle?: (entityId: string | null) => void
}) {
  const [showRevenueEvents, setShowRevenueEvents] = useState(false)
  const [showMetaVerification, setShowMetaVerification] = useState(false)
  const getEntityName = () => {
    switch (type) {
      case 'campaign':
        return entity.campaign_name || entity.name || 'Unnamed Campaign'
      case 'adset':
        return entity.adset_name || entity.name || 'Unnamed Ad Set'
      case 'ad':
        return entity.ad_name || entity.name || 'Unnamed Ad'
    }
  }

  const getEntityId = () => {
    switch (type) {
      case 'campaign':
        return entity.campaign_id
      case 'adset':
        return entity.adset_id
      case 'ad':
        return entity.ad_id
    }
  }

  return (
    <div
      className={`rounded-lg border border-gray-200 p-4 shadow-sm ${
        hasStats ? 'bg-white' : 'bg-gray-50 opacity-60'
      }`}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold ${hasStats ? 'text-gray-900' : 'text-gray-500'}`}>
            {getEntityName()}
          </div>
          <div className={`mt-0.5 text-xs ${hasStats ? 'text-gray-600' : 'text-gray-400'}`}>
            ID: {getEntityId()}
          </div>
          {type !== 'campaign' && entity.campaign_name && (
            <div className={`mt-0.5 text-xs ${hasStats ? 'text-gray-500' : 'text-gray-400'}`}>
              Campaign: {entity.campaign_name}
            </div>
          )}
        </div>
        {entity.status && (
          <span
            className={`ml-2 flex-shrink-0 rounded-full px-2 py-1 text-xs font-medium ${
              hasStats ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {entity.status}
          </span>
        )}
      </div>
      {!hasStats && (
        <div className="mb-3 text-xs italic text-gray-500">No performance data available</div>
      )}

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Impressions</span>
          </div>
          <div className="text-sm font-bold text-gray-900">
            {formatNumber(entity.impressions || 0)}
          </div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <MousePointer className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Clicks</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatNumber(entity.clicks || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Spend</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatCurrency(entity.spend || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <Percent className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">CTR</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatPercent(entity.ctr || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">CPC</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatCurrency(entity.cpc || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">CPM</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatCurrency(entity.cpm || 0)}</div>
        </div>

        {entity.revenue !== undefined && entity.revenue !== null && entity.revenue > 0 && (
          <div className="rounded-md bg-gray-50 p-2">
            <div className="mb-0.5 flex items-center gap-1">
              <DollarSign className="h-3 w-3 text-green-600" />
              <span className="text-xs font-medium text-gray-700">Revenue</span>
            </div>
            <div className="text-sm font-bold text-green-600">{formatCurrency(entity.revenue)}</div>
          </div>
        )}

        {entity.conversions !== undefined &&
          entity.conversions !== null &&
          entity.conversions > 0 && (
            <div className="rounded-md bg-gray-50 p-2">
              <div className="mb-0.5 flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Conversions</span>
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatNumber(entity.conversions)}
              </div>
            </div>
          )}

        {entity.roas !== undefined && entity.roas !== null && entity.roas > 0 && (
          <div className="rounded-md bg-gray-50 p-2">
            <div className="mb-0.5 flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-blue-600" />
              <span className="text-xs font-medium text-gray-700">ROAS</span>
            </div>
            <div className="text-sm font-bold text-blue-600">{entity.roas.toFixed(2)}x</div>
          </div>
        )}

        {entity.meta_roas !== undefined && entity.meta_roas !== null && entity.meta_roas > 0 && (
          <div className="rounded-md bg-gray-50 p-2">
            <div className="mb-0.5 flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-purple-600" />
              <span className="text-xs font-medium text-gray-700">Meta ROAS</span>
            </div>
            <div className="text-sm font-bold text-purple-600">{entity.meta_roas.toFixed(2)}x</div>
          </div>
        )}
      </div>

      {/* Revenue Events Section */}
      {hasStats &&
        entity.action_values &&
        Array.isArray(entity.action_values) &&
        entity.action_values.filter(
          (av: any) => av.action_type === 'omni_purchase' && parseFloat(av.value || '0') > 0
        ).length > 0 && (
          <div className="mt-3 border-t border-gray-200 pt-3">
            <button
              onClick={() => setShowRevenueEvents(!showRevenueEvents)}
              className="flex w-full items-center justify-between text-left"
            >
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">Revenue Events</span>
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                  {
                    entity.action_values.filter(
                      (av: any) =>
                        av.action_type === 'omni_purchase' && parseFloat(av.value || '0') > 0
                    ).length
                  }
                </span>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${showRevenueEvents ? 'rotate-180' : ''}`}
              />
            </button>

            {showRevenueEvents && (
              <div className="mt-3 space-y-2">
                {entity.action_values
                  .filter(
                    (av: any) =>
                      av.action_type === 'omni_purchase' && parseFloat(av.value || '0') > 0
                  )
                  .map((purchaseEvent: any, index: number) => {
                    const value = parseFloat(purchaseEvent.value || '0')
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-500"></div>
                          <span className="text-sm font-medium text-green-900">
                            Purchase Event #{index + 1}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-green-900">
                          {formatCurrency(value)}
                        </span>
                      </div>
                    )
                  })}
                <div className="mt-2 flex items-center justify-between rounded-lg border border-green-300 bg-green-100 px-3 py-2">
                  <span className="text-sm font-semibold text-green-900">Total Revenue</span>
                  <span className="text-sm font-bold text-green-900">
                    {formatCurrency(
                      entity.action_values
                        .filter((av: any) => av.action_type === 'omni_purchase')
                        .reduce((sum: number, av: any) => sum + parseFloat(av.value || '0'), 0)
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Meta Verification Section */}
      {hasStats &&
        (entity.action_values ||
          entity.purchase_roas ||
          entity.meta_reported_revenue !== undefined) && (
          <div className="mt-3 border-t border-gray-200 pt-3">
            <button
              onClick={() => setShowMetaVerification(!showMetaVerification)}
              className="flex w-full items-center justify-between text-left"
            >
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-900">Meta Verification</span>
                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                  Raw Data
                </span>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${showMetaVerification ? 'rotate-180' : ''}`}
              />
            </button>

            {showMetaVerification && (
              <div className="mt-3 space-y-3">
                {/* Comparison Table */}
                <div className="overflow-hidden rounded-lg border border-purple-200">
                  <div className="bg-purple-50 px-3 py-2">
                    <h4 className="text-sm font-semibold text-purple-900">
                      Calculated vs Meta Reported
                    </h4>
                  </div>
                  <div className="bg-white p-3">
                    <div className="space-y-2 text-sm">
                      {/* Revenue Comparison */}
                      {entity.revenue !== undefined &&
                        entity.meta_reported_revenue !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-700">Revenue:</span>
                            <div className="flex items-center gap-3 text-right">
                              <span className="text-gray-600">
                                {formatCurrency(entity.revenue)}
                              </span>
                              <span className="font-medium text-purple-600">
                                {formatCurrency(entity.meta_reported_revenue)}
                              </span>
                              {Math.abs(entity.revenue - entity.meta_reported_revenue) < 0.01 ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : Math.abs(
                                  (entity.revenue - entity.meta_reported_revenue) /
                                    Math.max(entity.revenue, entity.meta_reported_revenue, 1)
                                ) < 0.05 ? (
                                <AlertCircle className="h-4 w-4 text-yellow-500" />
                              ) : (
                                <AlertCircle className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          </div>
                        )}

                      {/* ROAS Comparison */}
                      {entity.roas !== undefined && entity.meta_roas !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-700">ROAS:</span>
                          <div className="flex items-center gap-3 text-right">
                            <span className="text-gray-600">{entity.roas.toFixed(2)}x</span>
                            <span className="font-medium text-purple-600">
                              {entity.meta_roas.toFixed(2)}x
                            </span>
                            {Math.abs(entity.roas - entity.meta_roas) < 0.01 ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : Math.abs(
                                (entity.roas - entity.meta_roas) /
                                  Math.max(entity.roas, entity.meta_roas, 1)
                              ) < 0.05 ? (
                              <AlertCircle className="h-4 w-4 text-yellow-500" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Legend */}
                    <div className="mt-3 flex items-center gap-4 border-t border-gray-200 pt-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-600">Calculated</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-purple-600">Meta Reported</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span>Match</span>
                        <AlertCircle className="h-3 w-3 text-yellow-500" />
                        <span>~5%</span>
                        <AlertCircle className="h-3 w-3 text-red-500" />
                        <span>Divergent</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Raw Meta Data */}
                {entity.action_values &&
                  Array.isArray(entity.action_values) &&
                  entity.action_values.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <h5 className="mb-2 text-sm font-semibold text-gray-900">
                        Raw Action Values
                      </h5>
                      <div className="space-y-1 text-xs">
                        {entity.action_values
                          .filter((av: any) => parseFloat(av.value || '0') > 0)
                          .map((action: any, index: number) => (
                            <div key={index} className="flex justify-between">
                              <span className="text-gray-600">{action.action_type}</span>
                              <span className="font-mono text-gray-900">
                                {action.action_type === 'omni_purchase'
                                  ? formatCurrency(action.value)
                                  : action.value}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {entity.purchase_roas &&
                  Array.isArray(entity.purchase_roas) &&
                  entity.purchase_roas.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <h5 className="mb-2 text-sm font-semibold text-gray-900">
                        Raw Purchase ROAS
                      </h5>
                      <div className="space-y-1 text-xs">
                        {entity.purchase_roas.map((roas: any, index: number) => (
                          <div key={index} className="flex justify-between">
                            <span className="text-gray-600">{roas.action_type}</span>
                            <span className="font-mono text-gray-900">
                              {parseFloat(roas.value).toFixed(2)}x
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

      {/* Chart Button (for all entity types with stats) */}
      {hasStats && onChartToggle && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <button
            onClick={() => {
              const entityId = getEntityId()
              if (entityId) {
                onChartToggle(expandedChart === entityId ? null : entityId)
              }
            }}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <BarChart3 className="h-4 w-4" />
            {expandedChart === getEntityId() ? 'Hide Chart' : 'View Performance Chart'}
          </button>
        </div>
      )}

      {/* Chart Display */}
      {expandedChart === getEntityId() && (
        <AdMetricsChart
          entityId={getEntityId() || ''}
          entityName={getEntityName()}
          entityType={type === 'campaign' ? 'campaign' : type === 'adset' ? 'adset' : 'ad'}
          platform="facebook"
        />
      )}
    </div>
  )
}

function downloadFacebookAdsInsights(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsInsightsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsInsights, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  // Handle both emitMode: 'single' and 'multi' (array of results)
  const data = parseResult.data
  const result: FacebookAdsInsightsResult = Array.isArray(data) ? data[0] : data

  let markdown = '# Meta Ads Insights & Recommendations\n\n'

  // Summary
  markdown += '## Summary\n\n'
  markdown += `${result.summary}\n\n`

  // Insights by priority
  const highPriority = result.insights.filter((i) => i.priority === 'high')
  const mediumPriority = result.insights.filter((i) => i.priority === 'medium')
  const lowPriority = result.insights.filter((i) => i.priority === 'low')

  if (highPriority.length > 0) {
    markdown += '## 🔴 High Priority Actions\n\n'
    highPriority.forEach((insight, index) => {
      markdown += `### ${index + 1}. ${insight.category}\n\n`
      markdown += `**Finding:** ${insight.finding}\n\n`
      markdown += `**Recommendation:** ${insight.recommendation}\n\n`
      markdown += `**Impact:** ${insight.impact}\n\n`
    })
  }

  if (mediumPriority.length > 0) {
    markdown += '## 🟡 Medium Priority Actions\n\n'
    mediumPriority.forEach((insight, index) => {
      markdown += `### ${index + 1}. ${insight.category}\n\n`
      markdown += `**Finding:** ${insight.finding}\n\n`
      markdown += `**Recommendation:** ${insight.recommendation}\n\n`
      markdown += `**Impact:** ${insight.impact}\n\n`
    })
  }

  if (lowPriority.length > 0) {
    markdown += '## 🟢 Low Priority Actions\n\n'
    lowPriority.forEach((insight, index) => {
      markdown += `### ${index + 1}. ${insight.category}\n\n`
      markdown += `**Finding:** ${insight.finding}\n\n`
      markdown += `**Recommendation:** ${insight.recommendation}\n\n`
      markdown += `**Impact:** ${insight.impact}\n\n`
    })
  }

  // Top Performers
  if (result.topPerformers && result.topPerformers.length > 0) {
    markdown += '## ✅ Top Performers\n\n'
    result.topPerformers.forEach((performer) => {
      markdown += `- **${performer.name}** (${performer.id}): ${performer.metric} = ${performer.value}\n`
    })
    markdown += '\n'
  }

  // Underperformers
  if (result.underperformers && result.underperformers.length > 0) {
    markdown += '## ⚠️ Needs Attention\n\n'
    result.underperformers.forEach((performer) => {
      markdown += `- **${performer.name}** (${performer.id}): ${performer.metric} = ${performer.value}\n`
    })
    markdown += '\n'
  }

  // Entity Performance Breakdown
  if (result.performanceData) {
    markdown += '## Entity Performance Breakdown\n\n'

    // Campaigns
    if (result.performanceData.campaigns && result.performanceData.campaigns.length > 0) {
      markdown += '### Campaigns\n\n'
      result.performanceData.campaigns.forEach((campaign) => {
        markdown += `**${campaign.campaign_name || 'Unnamed'}** (ID: ${campaign.campaign_id})\n`
        markdown += `- Impressions: ${(campaign.impressions || 0).toLocaleString()}\n`
        markdown += `- Clicks: ${(campaign.clicks || 0).toLocaleString()}\n`
        markdown += `- Spend: $${(campaign.spend || 0).toFixed(2)}\n`
        markdown += `- CTR: ${(campaign.ctr || 0).toFixed(2)}%\n`
        markdown += `- CPC: $${(campaign.cpc || 0).toFixed(2)}\n`
        markdown += `- CPM: $${(campaign.cpm || 0).toFixed(2)}\n`
        if (campaign.revenue !== undefined && campaign.revenue !== null && campaign.revenue > 0) {
          markdown += `- Revenue: $${campaign.revenue.toFixed(2)}\n`
        }
        if (
          campaign.conversions !== undefined &&
          campaign.conversions !== null &&
          campaign.conversions > 0
        ) {
          markdown += `- Conversions: ${campaign.conversions.toLocaleString()}\n`
        }
        if (campaign.roas !== undefined && campaign.roas !== null && campaign.roas > 0) {
          markdown += `- ROAS: ${campaign.roas.toFixed(2)}x\n`
        }
        markdown += '\n'
      })
    }

    // Ad Sets
    if (result.performanceData.adsets && result.performanceData.adsets.length > 0) {
      markdown += '### Ad Sets\n\n'
      result.performanceData.adsets.forEach((adset) => {
        markdown += `**${adset.adset_name || 'Unnamed'}** (ID: ${adset.adset_id})\n`
        markdown += `- Campaign: ${adset.campaign_name || 'N/A'}\n`
        markdown += `- Impressions: ${(adset.impressions || 0).toLocaleString()}\n`
        markdown += `- Clicks: ${(adset.clicks || 0).toLocaleString()}\n`
        markdown += `- Spend: $${(adset.spend || 0).toFixed(2)}\n`
        markdown += `- CTR: ${(adset.ctr || 0).toFixed(2)}%\n`
        markdown += `- CPC: $${(adset.cpc || 0).toFixed(2)}\n`
        if (adset.revenue !== undefined && adset.revenue !== null && adset.revenue > 0) {
          markdown += `- Revenue: $${adset.revenue.toFixed(2)}\n`
        }
        if (
          adset.conversions !== undefined &&
          adset.conversions !== null &&
          adset.conversions > 0
        ) {
          markdown += `- Conversions: ${adset.conversions.toLocaleString()}\n`
        }
        if (adset.roas !== undefined && adset.roas !== null && adset.roas > 0) {
          markdown += `- ROAS: ${adset.roas.toFixed(2)}x\n`
        }
        markdown += '\n'
      })
    }

    // Ads
    if (result.performanceData.ads && result.performanceData.ads.length > 0) {
      markdown += '### Ads\n\n'
      result.performanceData.ads.forEach((ad) => {
        markdown += `**${ad.ad_name || 'Unnamed'}** (ID: ${ad.ad_id})\n`
        markdown += `- Campaign: ${ad.campaign_name || 'N/A'}\n`
        markdown += `- Ad Set: ${ad.adset_name || 'N/A'}\n`
        markdown += `- Impressions: ${(ad.impressions || 0).toLocaleString()}\n`
        markdown += `- Clicks: ${(ad.clicks || 0).toLocaleString()}\n`
        markdown += `- Spend: $${(ad.spend || 0).toFixed(2)}\n`
        markdown += `- CTR: ${(ad.ctr || 0).toFixed(2)}%\n`
        markdown += `- CPC: $${(ad.cpc || 0).toFixed(2)}\n`
        if (ad.revenue !== undefined && ad.revenue !== null && ad.revenue > 0) {
          markdown += `- Revenue: $${ad.revenue.toFixed(2)}\n`
        }
        if (ad.conversions !== undefined && ad.conversions !== null && ad.conversions > 0) {
          markdown += `- Conversions: ${ad.conversions.toLocaleString()}\n`
        }
        if (ad.roas !== undefined && ad.roas !== null && ad.roas > 0) {
          markdown += `- ROAS: ${ad.roas.toFixed(2)}x\n`
        }
        markdown += '\n'
      })
    }
  }

  return markdown
}

export const FacebookAdsInsightsDisplay = createWorkflowToolComponent(
  FacebookAdsInsightsDisplayComponent,
  downloadFacebookAdsInsights,
  facebookAdsInsightsToMarkdown
)
